import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, ShieldCheck, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { certifyPublicApi, getDisplayMessage } from '@/api/apiClient';

const SESSION_PREFIX = 'certify_verify_session:';

/** Limite prudente para buffer contíguo no browser (~1,9 GiB). */
const MAX_PREALLOC_BYTES = 1_900_000_000;
/** Acima disto: File System Access API grava no disco (Chrome/Edge), sem segurar o ZIP inteiro na RAM. */
const SAVE_PICKER_THRESHOLD = 32 * 1024 * 1024;
/** Agrega chunks antes do `Blob` final (evita milhares de partes). */
const BLOB_PART_TARGET = 16 * 1024 * 1024;
/** Alinhado ao `highWaterMark` do stream na API — poucas escritas no disco via File System Access API. */
const DISK_WRITE_FLUSH_BYTES = 16 * 1024 * 1024;

/** Monta um `Blob` a partir do corpo da resposta com menos cópias que milhares de micro-chunks. */
async function blobFromResponseBody(res, opts) {
  const { total, pctKnown, flushDownloadUi, throttleRef, maxPrealloc, partTarget } = opts;
  const reader = res.body.getReader();
  const ct = res.headers.get('Content-Type') || undefined;

  if (pctKnown && total > 0 && total <= maxPrealloc) {
    const out = new Uint8Array(total);
    let offset = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value?.byteLength) continue;
      const room = total - offset;
      if (room <= 0) break;
      const n = Math.min(value.byteLength, room);
      out.set(value.subarray(0, n), offset);
      offset += n;
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      if (now - throttleRef.current >= 120 || offset === total) {
        throttleRef.current = now;
        flushDownloadUi(offset, total, pctKnown);
      }
    }
    flushDownloadUi(offset, total, pctKnown);
    return new Blob([new Uint8Array(out.buffer, 0, offset)], { type: ct });
  }

  const megaParts = [];
  let bucket = [];
  let bucketSize = 0;
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value?.byteLength) continue;
    loaded += value.byteLength;
    bucket.push(value);
    bucketSize += value.byteLength;
    if (bucketSize >= partTarget) {
      megaParts.push(new Blob(bucket, { type: ct }));
      bucket = [];
      bucketSize = 0;
    }
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - throttleRef.current >= 120) {
      throttleRef.current = now;
      flushDownloadUi(loaded, pctKnown ? total : null, pctKnown);
    }
  }
  if (bucket.length) megaParts.push(new Blob(bucket, { type: ct }));
  flushDownloadUi(loaded, pctKnown ? total : null, pctKnown);
  return new Blob(megaParts, { type: ct });
}

export default function CertifyVerify() {
  const { id: evidenceId } = useParams();
  const [meta, setMeta] = useState(null);
  const [metaErr, setMetaErr] = useState('');
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [fileDownloading, setFileDownloading] = useState(false);
  /** null inactivo; 0–100 com tamanho conhecido; -1 indeterminado (barra animada) */
  const [fileDownloadProgress, setFileDownloadProgress] = useState(null);
  const [downloadBytesLoaded, setDownloadBytesLoaded] = useState(0);
  const [downloadBytesTotal, setDownloadBytesTotal] = useState(null);
  const downloadUiThrottleRef = useRef(0);

  const storageKey = evidenceId ? `${SESSION_PREFIX}${evidenceId}` : '';

  useEffect(() => {
    if (!evidenceId) return;
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      setMetaErr('');
      try {
        const m = await certifyPublicApi.meta(evidenceId);
        if (!cancelled) setMeta(m);
      } catch (e) {
        if (!cancelled) setMetaErr(getDisplayMessage(e, 'Registro não encontrado'));
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [evidenceId]);

  useEffect(() => {
    if (!evidenceId || !storageKey) return;
    try {
      const t = sessionStorage.getItem(storageKey);
      if (t) setSessionToken(t);
    } catch {
      // ignore
    }
  }, [evidenceId, storageKey]);

  useEffect(() => {
    if (!sessionToken || !evidenceId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const d = await certifyPublicApi.detail(evidenceId, sessionToken);
        if (!cancelled) setDetail(d);
      } catch (e) {
        if (!cancelled) {
          setDetail(null);
          try {
            sessionStorage.removeItem(storageKey);
          } catch {
            // ignore
          }
          setSessionToken('');
          toast.message(getDisplayMessage(e, 'Sessão expirada. Informe a senha novamente.'));
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionToken, evidenceId, storageKey]);

  async function onVerify(e) {
    e.preventDefault();
    if (!evidenceId || !password.trim()) return;
    setVerifying(true);
    try {
      const res = await certifyPublicApi.verifyWithPassword(evidenceId, password.trim());
      const token = res?.session_token;
      if (!token) throw new Error('Resposta inválida da API.');
      sessionStorage.setItem(storageKey, token);
      setSessionToken(token);
      toast.success('Verificação concluída.');
      setPassword('');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Senha incorreta'));
    } finally {
      setVerifying(false);
    }
  }

  function flushDownloadUi(loaded, total, pctKnown) {
    setDownloadBytesLoaded(loaded);
    if (total != null && Number.isFinite(total) && total > 0) setDownloadBytesTotal(total);
    if (pctKnown && total > 0) {
      setFileDownloadProgress(Math.min(100, Math.round((loaded / total) * 100)));
    }
  }

  async function downloadFile() {
    if (!evidenceId || !sessionToken) return;
    if (detail?.file_download_available === false) {
      toast.error(detail?.file_download_unavailable_reason || 'Descarga indisponível.');
      return;
    }
    setFileDownloading(true);
    setFileDownloadProgress(null);
    setDownloadBytesLoaded(0);
    setDownloadBytesTotal(null);
    downloadUiThrottleRef.current = 0;
    const proxyFallbackUrl = certifyPublicApi.fileUrl(evidenceId);
    async function fetchBinary() {
      let duData = {};
      try {
        const du = await certifyPublicApi.downloadUrl(evidenceId, sessionToken);
        duData = du && typeof du === 'object' ? du : {};
      } catch {
        /* segue para relay */
      }
      const relayUrl =
        typeof duData.relay_url === 'string' && duData.relay_url.trim().length > 0
          ? duData.relay_url.trim()
          : proxyFallbackUrl;
      const presignedUrl =
        duData.mode === 'presigned' && typeof duData.url === 'string' && duData.url.length > 0
          ? duData.url
          : null;
      if (presignedUrl) {
        try {
          const r = await fetch(presignedUrl, { method: 'GET', mode: 'cors', cache: 'no-store' });
          if (r.ok) return r;
        } catch {
          /* CORS/rede/etc. — usar relay garantido pela API */
        }
      }
      return fetch(relayUrl, {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
    }
    try {
      const res = await fetchBinary();
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = data.error || res.statusText || 'Erro ao descarregar';
        const code = typeof data.code === 'string' ? data.code : '';
        throw Object.assign(new Error(msg), { status: res.status, data, code });
      }

      const lenHeader = res.headers.get('Content-Length');
      const totalFromHeader = lenHeader ? parseInt(lenHeader, 10) : NaN;
      const totalFromDetail =
        detail?.file_size != null && Number(detail.file_size) > 0 ? Number(detail.file_size) : NaN;
      const total =
        Number.isFinite(totalFromHeader) && totalFromHeader > 0 ? totalFromHeader : totalFromDetail;
      const pctKnown = Number.isFinite(total) && total > 0;
      setDownloadBytesTotal(pctKnown ? total : null);
      setFileDownloadProgress(pctKnown ? 0 : -1);

      const suggestedName = detail?.file_name || `evidencia_${evidenceId.slice(0, 8)}`;
      const sizeHint = pctKnown ? total : Number(detail?.file_size) || 0;
      const trySavePicker =
        typeof window.showSaveFilePicker === 'function' &&
        res.body &&
        sizeHint >= SAVE_PICKER_THRESHOLD;

      let blob = null;
      let readerStarted = false;

      if (trySavePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: suggestedName,
            types: [{ description: 'Arquivo', accept: { '*/*': ['.*'] } }],
          });
          const writable = await handle.createWritable();
          const reader = res.body.getReader();
          readerStarted = true;
          const pickerCt = res.headers.get('Content-Type') || undefined;
          let loaded = 0;
          let diskBuf = [];
          let diskBufSize = 0;
          const flushDiskBuf = async () => {
            if (!diskBuf.length) return;
            await writable.write(new Blob(diskBuf, { type: pickerCt }));
            diskBuf = [];
            diskBufSize = 0;
          };
          for (;;) {
            const { done, value } = await reader.read();
            if (done) {
              await flushDiskBuf();
              break;
            }
            if (value?.byteLength) {
              diskBuf.push(value);
              diskBufSize += value.byteLength;
              loaded += value.byteLength;
              if (diskBufSize >= DISK_WRITE_FLUSH_BYTES) {
                await flushDiskBuf();
              }
            }
            const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
            if (now - downloadUiThrottleRef.current >= 120) {
              downloadUiThrottleRef.current = now;
              flushDownloadUi(loaded, pctKnown ? total : null, pctKnown);
            }
          }
          flushDownloadUi(loaded, pctKnown ? total : null, pctKnown);
          await writable.close();
          setFileDownloadProgress(pctKnown ? 100 : null);
          toast.success('Ficheiro guardado.');
          return;
        } catch (pickerErr) {
          if (readerStarted) {
            toast.message('A concluir por download no navegador…');
            const res2 = await fetchBinary();
            if (!res2.ok) {
              const data = await res2.json().catch(() => ({}));
              throw new Error(data.error || res2.statusText);
            }
            blob = await blobFromResponseBody(res2, {
              total,
              pctKnown,
              flushDownloadUi,
              throttleRef: downloadUiThrottleRef,
              maxPrealloc: MAX_PREALLOC_BYTES,
              partTarget: BLOB_PART_TARGET,
            });
          } else if (pickerErr?.name !== 'AbortError') {
            console.warn('[CertifyVerify] showSaveFilePicker:', pickerErr);
          }
        }
      }

      if (!blob) {
        if (!res.body || typeof res.body.getReader !== 'function') {
          blob = await res.blob();
          flushDownloadUi(blob.size, blob.size, true);
        } else {
          blob = await blobFromResponseBody(res, {
            total,
            pctKnown,
            flushDownloadUi,
            throttleRef: downloadUiThrottleRef,
            maxPrealloc: MAX_PREALLOC_BYTES,
            partTarget: BLOB_PART_TARGET,
          });
        }
      }

      setFileDownloadProgress(pctKnown ? 100 : null);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = suggestedName;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      const hint =
        err && typeof err === 'object' && 'data' in err && err.data && typeof err.data.hint === 'string'
          ? err.data.hint
          : '';
      const primary = getDisplayMessage(err, 'Falha ao descarregar');
      if (hint) {
        toast.error(primary, { description: hint });
      } else {
        toast.error(primary);
      }
    } finally {
      setFileDownloading(false);
      setFileDownloadProgress(null);
      setDownloadBytesLoaded(0);
      setDownloadBytesTotal(null);
    }
  }

  function logoutVerify() {
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    setSessionToken('');
    setDetail(null);
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <CardTitle>Verificação AuctionCertify</CardTitle>
          <CardDescription>Consulta forense pública protegida por senha derivada do hash.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loadingMeta ? (
            <div className="flex justify-center py-10 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : metaErr ? (
            <p className="text-center text-sm text-destructive">{metaErr}</p>
          ) : (
            <>
              <div className="rounded-lg border bg-card p-4 text-sm space-y-1">
                <p className="font-medium">{meta?.titulo}</p>
                <p className="text-muted-foreground">{meta?.tipo}</p>
              </div>

              {!sessionToken ? (
                <form onSubmit={onVerify} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="vpw">Primeiros 4 caracteres hex do SHA-256</Label>
                    <Input
                      id="vpw"
                      value={password}
                      onChange={(e) => setPassword(e.target.value.replace(/[^a-fA-F0-9]/g, '').slice(0, 4))}
                      placeholder="Ex.: a3f9"
                      autoComplete="off"
                      maxLength={4}
                      className="font-mono tracking-wider"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={verifying || password.trim().length < 4}>
                    {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Verificar
                  </Button>
                </form>
              ) : detailLoading ? (
                <div className="flex justify-center py-6 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : detail ? (
                <div className="space-y-4 text-sm">
                  <div className="rounded-md border p-4 space-y-2">
                    <Row label="Empresa" value={detail.empresa_razao_social} />
                    <Row label="Selado em" value={new Date(detail.sealed_at).toLocaleString()} />
                    <Row label="Ficheiro" value={`${detail.file_name} (${detail.file_size} bytes)`} />
                    <Row label="SHA-256" value={<code className="text-xs break-all">{detail.hash_sha256}</code>} />
                    <Row
                      label="Âncora"
                      value={
                        <code className="block text-xs break-all whitespace-pre-wrap font-mono">
                          {detail.blockchain_tx || '—'}
                        </code>
                      }
                    />
                    <Row label="IP (referência selagem)" value={detail.captured_ip || '—'} />
                  </div>
                  {detail.descricao ? <p>{detail.descricao}</p> : null}
                  {detail.source_url ? (
                    <p>
                      URL:{' '}
                      <a href={detail.source_url} className="text-primary underline break-all" target="_blank" rel="noreferrer">
                        {detail.source_url}
                      </a>
                    </p>
                  ) : null}
                  {detail.file_download_available === false && detail.file_download_unavailable_reason ? (
                    <div
                      className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100"
                      role="status"
                    >
                      {detail.file_download_unavailable_reason}
                    </div>
                  ) : null}
                  {detail.file_download_available && detail.download_mode ? (
                    <p className="text-xs text-muted-foreground leading-relaxed rounded-md border bg-muted/30 px-3 py-2">
                      {detail.download_mode === 'presigned' ? (
                        <>
                          <span className="font-medium text-foreground">Descarga: directa ao MinIO/S3</span>
                          {detail.download_storage_host ? (
                            <> (host: <code className="text-[11px]">{detail.download_storage_host}</code>)</>
                          ) : null}
                          . O browser liga ao armazenamento sem passar pelo stream da API — em geral o melhor débito.
                        </>
                      ) : (
                        <>
                          <span className="font-medium text-foreground">Descarga: via API (proxy)</span>. O ficheiro
                          atravessa o servidor da aplicação; se o débito for baixo, no Coolify/Nginx verifique{' '}
                          <code className="text-[11px]">proxy_buffering</code>, timeouts e limites para respostas grandes.
                        </>
                      )}
                    </p>
                  ) : null}
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={fileDownloading || detail.file_download_available === false}
                        className="sm:flex-1"
                        onClick={downloadFile}
                      >
                        {fileDownloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                        {fileDownloading ? 'A transferir…' : 'Descarregar arquivo original'}
                      </Button>
                      <Button type="button" variant="outline" disabled={fileDownloading} className="sm:flex-1" onClick={logoutVerify}>
                        Encerrar sessão de verificação
                      </Button>
                    </div>
                    {!fileDownloading &&
                    typeof window.showSaveFilePicker === 'function' &&
                    Number(detail.file_size) >= SAVE_PICKER_THRESHOLD ? (
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Ficheiros deste tamanho: o Chrome/Edge podem pedir onde guardar — a gravação vai directa para o disco, sem
                        ocupar centenas de MB na memória do separador (mais rápido e estável).
                      </p>
                    ) : null}
                    {fileDownloading ? (
                      <div
                        className="rounded-lg border bg-muted/40 px-3 py-3 space-y-2"
                        role="status"
                        aria-live="polite"
                        aria-label="Estado do download"
                      >
                        <p className="text-xs font-medium text-foreground">Estado do download</p>
                        {fileDownloadProgress != null && fileDownloadProgress >= 0 ? (
                          <Progress value={fileDownloadProgress} className="h-3" />
                        ) : (
                          <div className="relative h-3 w-full overflow-hidden rounded-full bg-primary/20">
                            <div className="h-full w-1/3 rounded-full bg-primary animate-progress-loading" />
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground text-center tabular-nums">
                          {fileDownloadProgress != null && fileDownloadProgress >= 0 ? (
                            <>
                              <span className="font-medium text-foreground">{fileDownloadProgress}%</span>
                              {' · '}
                              {formatCertifyBytes(downloadBytesLoaded)}
                              {downloadBytesTotal != null ? ` de ${formatCertifyBytes(downloadBytesTotal)}` : null}
                            </>
                          ) : (
                            <>
                              A transferir…{' '}
                              {downloadBytesLoaded > 0 ? (
                                <>
                                  <span className="font-medium text-foreground">{formatCertifyBytes(downloadBytesLoaded)}</span>
                                  {downloadBytesTotal != null
                                    ? ` de ${formatCertifyBytes(downloadBytesTotal)}`
                                    : ' (tamanho total desconhecido)'}
                                </>
                              ) : (
                                'aguarde.'
                              )}
                            </>
                          )}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="min-w-0">
      <span className="text-muted-foreground text-xs uppercase tracking-wide">{label}</span>
      <div className="font-medium break-words min-w-0">{value}</div>
    </div>
  );
}

function formatCertifyBytes(n) {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1000) return `${Math.round(n)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  const digits = u === 0 ? 0 : v >= 100 ? 0 : v >= 10 ? 1 : 2;
  return `${v.toFixed(digits)} ${units[u]}`;
}
